import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Customer } from '@usf/customer-types';
import { MyOrdersStoreService, Order, OrderHeader } from '@usf/ngrx-order';
import { Observable, of } from 'rxjs';
import {
  take,
  filter,
  tap,
  switchMap,
  map,
  debounceTime,
} from 'rxjs/operators';
import { PanAppState } from '@panamax/app-state';
import { OrderService } from './order.service';
import { CustomerStoreService } from 'src/app/ngrx-customer/services';
import { OrderInsightsService } from './order-insights.service';
import { CustomerSwitchStatus } from '@app/ngrx-customer/constants/customer-switch-status';

@Injectable({
  providedIn: 'root',
})
export class OrderInsightsResolverService {
  loading = false;
  selectedCustomer: Customer;

  constructor(
    protected panAppState: PanAppState,
    private myOrdersStoreService: MyOrdersStoreService,
    private orderService: OrderService,
    private orderInsightsService: OrderInsightsService,
    private customerStoreService: CustomerStoreService,
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<string | null> {
    const orderId = route.paramMap.get('orderId');

    return this.switchToCustomerAndLoadOrder(orderId);
  }

  switchToCustomerAndLoadOrder(orderId: string): Observable<string | null> {
    // Ensure the order id contains the customer number, department number, and order server id
    const values = orderId ? orderId.split(':') : [];
    if (values.length !== 3) {
      this.handleError(orderId);
      return of(null);
    }
    const customerNumber = parseInt(values[0]) ?? undefined;
    const departmentNumber = parseInt(values[1]) ?? undefined;
    const orderServerId = values[2] ?? undefined;

    // Validate that the values are valid numbers
    if (
      (!customerNumber && customerNumber !== 0) ||
      (!departmentNumber && departmentNumber !== 0) ||
      !orderServerId
    ) {
      this.handleError(orderId);
      return of(null);
    }

    // Get the customer with the customer number
    return this.customerStoreService.customerWithNumber$(customerNumber).pipe(
      filter(customer => !!customer),
      take(1),
      switchMap(customer => {
        // Dispatch the switch to customer action if needed.
        this.orderService.switchToCustomerIfNeeded(
          customer as Customer,
          departmentNumber,
        );
        // Customer Switch Status must be complete before loading order data and
        // resolving the route. Resolving will allow navigation to the order insights page.
        return this.customerStoreService.customerSwitchStatus$().pipe(
          debounceTime(250),
          filter(
            status => !!status && status === CustomerSwitchStatus.complete,
          ),
          take(1),
          tap(() => {
            this.selectOrderData(orderId);
          }),
          map(() => orderId),
        );
      }),
    );
  }

  checkCustomerIsAvailable(customerNumber: number, orderId: string) {
    this.customerStoreService
      .loadCustomers$()
      .pipe(
        filter(customers => !!customers.length),
        take(1),
      )
      .subscribe(customers => {
        const customer = customers.find(
          customer => customer.customerNumber === customerNumber,
        );
        if (!customer) {
          this.handleError(orderId);
        }
      });
  }

  selectOrderData(orderId: string) {
    if (orderId) {
      // First, ensure customer orders are loaded
      this.myOrdersStoreService.retrieveCustomerOrders();

      this.myOrdersStoreService
        .getOrderWithId(orderId)
        .pipe(
          filter(order => !!order),
          take(1),
        )
        .subscribe(order => {
          if (order) {
            this.loadInsightsProducts(order);
          } else {
            this.handleError(orderId);
          }
        });
    } else {
      this.handleError(orderId);
    }
  }

  loadInsightsProducts(order: Order): void {
    // Get all product numbers including order items to load, consistent with loadOrderAndProductData$
    const insightsProducts = this.orderInsightsService.getAllInsightsProducts(
      order.orderHeader,
    );
    const orderItemProductNumbers =
      order.orderItems?.map(item => item.productNumber) || [];
    const allProductNumbers = Array.from(
      new Set([...insightsProducts, ...orderItemProductNumbers]),
    );

    if (allProductNumbers.length > 0) {
      return this.orderInsightsService.loadProducts(allProductNumbers);
    }
  }

  //will add additional error handling (like an error popup) based on TBD business logic
  handleError(orderId: string) {
    console.error('Error loading order details: ', orderId);
  }
}
